import { yup } from 'common/validation/initYup';
import { ValidationSchema } from 'common/validation/ValidationSchema';
import { Type, Transform, plainToClass, classToPlain } from 'class-transformer';
import { TypeTransformDecimal } from 'common/dto/shared/types/TypeTransformDecimal';
import { Decimal } from 'decimal.js-light';
import { ApiProperty } from '@nestjs/swagger';
import type { IDto, IDtoPartial } from 'common/dto/shared/DtoInterfaces';
import { PaginatedMetaDto } from 'common/dto/shared/PaginatedDto';
import { Roles } from 'common/permissions/Roles';
import { ValidationAction } from 'common/validation/ValidationAction';
import { OfficeDto } from './OfficeDto';

/**
 * Rappresentazione DTO della classe User definita in: src/server/schema/user/user/User.entity.ts
 * Hash: 81a5a5874541ac152c3f13e53007296f
 */
@ValidationSchema(() => UserSchema)
export class UserDto {
  @ApiProperty({ required: false, type: Number })
  id!: number;
  @ApiProperty({ type: String, description: 'Login Utente' })
  login!: string;
  @ApiProperty({ type: String, description: 'Nome Utente' })
  name!: string;
  @ApiProperty({ description: 'Ruolo' })
  role!: "SuperAdmin" | "System" | "DocumentalUser" | "LdapUser" | "DecenteredUser" | "DecenteredSupportUser" | "RogatoryUser" | "RogatoryAdmin";
  @ApiProperty({ type: Boolean, description: 'Autorizzazione attraverso password' })
  enablePassword!: boolean;
  @ApiProperty({ required: false, type: String, description: 'Password' })
  password?: string;
  @ApiProperty({ required: false, type: String })
  serviceToken?: string | null | undefined;
  @ApiProperty({ required: false, type: String, description: 'Codice utente documentale' })
  serial?: string | null | undefined;
  @ApiProperty({ required: false, type: Number, description: 'ID Originale migrazione' })
  migrationId?: number | null | undefined;
  @ApiProperty({ required: false, type: Number })
  documentalUid?: number | undefined;
  @ApiProperty({ required: false })
  documentalRole?: "SuperAdmin" | "System" | "DocumentalUser" | "LdapUser" | "DecenteredUser" | "DecenteredSupportUser" | "RogatoryUser" | "RogatoryAdmin" | undefined;
  @ApiProperty({ required: false, type: Number })
  documentalRoleId?: number | undefined;
  @ApiProperty({ required: false, type: String })
  documentalRoleCode?: string | undefined;
  @ApiProperty({ required: false, type: String })
  documentalRoleDescription?: string | undefined;
  @ApiProperty({ required: false, type: Number })
  documentalOfficeId?: number | undefined;
  @ApiProperty({ required: false, type: String })
  documentalOfficeCode?: string | undefined;
  @ApiProperty({ required: false, type: String })
  documentalOfficeDescription?: string | undefined;
  @ApiProperty({ required: false, type: String })
  foliumJwt?: string | undefined;
  @ApiProperty({ required: false, type: String })
  foliumRefreshJwt?: string | undefined;
  @ApiProperty({ required: false, type: () => OfficeDto })
  @Type(() => OfficeDto)
  office?: OfficeDto | null;
  @ApiProperty({ type: String })
  strategy!: string;
  @ApiProperty({ type: String })
  originalStrategy!: string;
  @ApiProperty({ required: false, type: String, format: 'date-time', description: 'Data di Creazione' })
  createdAt!: Date;
  @ApiProperty({ required: false, type: String, format: 'date-time', description: 'Data di Aggiornamento' })
  updatedAt!: Date;
  @ApiProperty({ required: false, type: String, format: 'date-time', description: 'Data di Eliminazione' })
  deletedAt?: Date | null | undefined;

  /** Proprietà che identifica i DTO */
  readonly __dto!: any;

  /**
   * Crea una nuova istanza con i valori forniti
   */
  constructor(values?: IDtoPartial<UserDto>) {
    if (values != null) {
      Object.assign(this, values instanceof UserDto ? values : plainToClass(UserDto, values));
    }
  }

  isRogatory() {
    return (
      this.getRole().hasPermission('rogatory') &&
      (this.office?.isRogatory ?? false)
    );
  }

  getRole() {
    return Roles[this.documentalRole ?? this.role];
  }

  isOfficeResponsible() {
    return this.office?.responsibleUserId === this.id;
  }

  async validate(options?: any) {
    const validated = await UserSchema.validate(classToPlain(this), options);
    return new UserDto(validated);
  }
}

/** Interfaccia simmetrica al DTO UserDto */
export type IUserType = IDto<UserDto>;

/**
 * DTO Paginato della classe User
 */
export class PaginatedUserDto {
  @ApiProperty({ type: [UserDto] })
  @Type(() => UserDto)
  items!: UserDto[];
  @ApiProperty({ type: PaginatedMetaDto })
  meta!: PaginatedMetaDto;
}

export const UserSchema = yup
  .object({
    id: yup.number(),
    login: yup.string().required().label('Login Utente'),
    name: yup.string().required().label('Nome Utente'),
    role: yup.mixed().required().label('Ruolo'),
    enablePassword: yup.boolean().required().label('Autorizzazione attraverso password'),
    password: yup.string().when(['$action', 'enablePassword'], {
          is: (action: string, enablePassword: boolean) =>
            action === ValidationAction.create && enablePassword,
          then: (schema: any) => schema.required()
        }).label('Password'),
    serviceToken: yup.string().nullable(),
    serial: yup.string().nullable().label('Codice utente documentale')
  })
  .noUnknown()
  .meta({ schemaName: "UserSchema" })
  .required();
